import React, { createContext, useContext, useState, useEffect } from 'react';
import { buildProductImageUrl } from '../utils/productImageUrl';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem('cart');
      return raw ? JSON.parse(raw) : [];
    } catch (err) { return []; }
  });
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, qty = 1) => {
    setAppliedCoupon(null);
    setCart(prev => {
      // For variations, use a combination of product ID and variation ID as unique key
      const cartItemKey = product.variationId ? `${product._id}-${product.variationId}` : product._id;

      const idx = prev.findIndex(i => {
        const itemKey = i.variationId ? `${i.product}-${i.variationId}` : i.product;
        return itemKey === cartItemKey;
      });

      if (idx > -1) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }

      const imageUrl = buildProductImageUrl(product.image || product.images?.[0], 'small');

      const effectivePrice = product.variationId ? product.price : (product.discountedPrice || product.price);
      const originalPrice = product.variationId ? null : (product.discountedPrice ? product.price : null);

      return [...prev, {
        product: product._id,
        name: product.name,
        price: effectivePrice,
        originalPrice,
        image: imageUrl || '',
        qty,
        size: product.size || null,
        variationId: product.variationId || null
      }];
    });
  };

  const updateQty = (productId, qty, variationId = null) => {
    const safeQty = Math.max(1, Number.parseInt(qty, 10) || 1);
    setAppliedCoupon(null);
    setCart(prev => prev.map(i => {
      const isSameItem = variationId
        ? (i.product === productId && i.variationId === variationId)
        : (i.product === productId && !i.variationId);
      return isSameItem ? { ...i, qty: safeQty } : i;
    }));
  };

  const removeFromCart = (productId, variationId = null) => {
    setAppliedCoupon(null);
    setCart(prev => prev.filter(i => {
      const isSameItem = variationId
        ? (i.product === productId && i.variationId === variationId)
        : (i.product === productId && !i.variationId);
      return !isSameItem;
    }));
  };
  const clearCart = () => {
    setAppliedCoupon(null);
    setCart([]);
  };
  const removeCoupon = () => setAppliedCoupon(null);

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      appliedCoupon,
      setAppliedCoupon,
      removeCoupon
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
export default CartContext;
